import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CONTENT_TYPES = ["EVENT", "GAME", "PARTNER_UPDATE", "COMMUNITY_BRIEF", "EXPERT_TIP"];
const AUDIENCES = ["ALL_MEMBERS", "CHAPTER_ONLY", "FOUNDING_MEMBERS", "INVITED"];
const LINKED_TYPES = ["EVENT", "GAME"];

export default function ContentNew() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: chapters = [] } = useQuery({ queryKey: ["chapters"], queryFn: () => api.get("/chapters") });
  const { data: events = [] } = useQuery({ queryKey: ["admin-events"], queryFn: () => api.get("/admin/events") });

  const [form, setForm] = useState({
    content_type: "COMMUNITY_BRIEF", title: "", subtitle: "", body: "",
    image_url: "", chapter_id: "", audience: "ALL_MEMBERS",
    publish_date: "", expiration_date: "", is_featured: false,
    source_event_id: "", source_game_id: "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLinked = LINKED_TYPES.includes(form.content_type);

  // Reset source fields when type changes
  useEffect(() => {
    if (form.content_type !== "EVENT") setForm((f) => ({ ...f, source_event_id: "" }));
    if (form.content_type !== "GAME") setForm((f) => ({ ...f, source_game_id: "" }));
  }, [form.content_type]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.content_type === "EVENT" && !form.source_event_id) { toast.error("Select a source event"); return; }
    if (form.content_type === "GAME" && !form.source_game_id) { toast.error("Select a source game"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        chapter_id: form.chapter_id || null,
        image_url: form.image_url || null,
        publish_date: form.publish_date || null,
        expiration_date: form.expiration_date || null,
        source_event_id: form.source_event_id || null,
        source_game_id: form.source_game_id || null,
        subtitle: form.subtitle || null,
        body: form.body || null,
      };
      const res = await api.post("/admin/content", payload);
      toast.success(`Content created as DRAFT`);
      qc.invalidateQueries({ queryKey: ["admin-content"] });
      navigate(`/admin/content/${res.id}`);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div data-testid="content-new-page">
      <Link to="/admin/content" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]">
        <ArrowLeft size={16} /> Content
      </Link>
      <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide mb-4">New Content</h1>
      <p className="text-xs text-lv-muted mb-6">Content is created as <span className="text-amber-400">DRAFT</span>. Event/Game content references their canonical records.</p>

      <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Content Type *</label>
            <Select value={form.content_type} onValueChange={(v) => update("content_type", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="content-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{CONTENT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Audience</label>
            <Select value={form.audience} onValueChange={(v) => update("audience", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{AUDIENCES.map((a) => <SelectItem key={a} value={a} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {form.content_type === "EVENT" && (
            <div className="sm:col-span-2">
              <label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Source Event *</label>
              <Select value={form.source_event_id} onValueChange={(v) => update("source_event_id", v)}>
                <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="source-event-select"><SelectValue placeholder="Select an event" /></SelectTrigger>
                <SelectContent className="bg-walnut-deep border-lv-border">
                  {events.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">
                      {ev.title} ({ev.status}) {ev.date ? `— ${ev.date}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-lv-muted/60 mt-1">This references the canonical event record. Edit the event itself for operational data.</p>
            </div>
          )}

          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Editorial Title *</label><input value={form.title} onChange={(e) => update("title", e.target.value)} className="lv-input" placeholder="Headline for the content feed" data-testid="content-title" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Subtitle</label><input value={form.subtitle} onChange={(e) => update("subtitle", e.target.value)} className="lv-input" placeholder="Optional subtitle or tagline" /></div>

          {!isLinked && (
            <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Body</label><textarea value={form.body} onChange={(e) => update("body", e.target.value)} className="lv-input min-h-[120px]" rows={5} placeholder="Content body..." data-testid="content-body" /></div>
          )}

          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Chapter</label>
            <Select value={form.chapter_id} onValueChange={(v) => update("chapter_id", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue placeholder="All chapters" /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{chapters.map((ch) => <SelectItem key={ch.id} value={ch.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{ch.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Image URL</label><input value={form.image_url} onChange={(e) => update("image_url", e.target.value)} className="lv-input" placeholder="Optional" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Publish Date</label><input type="date" value={form.publish_date} onChange={(e) => update("publish_date", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Expiration Date</label><input type="date" value={form.expiration_date} onChange={(e) => update("expiration_date", e.target.value)} className="lv-input" /></div>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" onClick={() => update("is_featured", !form.is_featured)} className={`relative w-10 h-5 rounded-full transition-colors ${form.is_featured ? "bg-bronze" : "bg-walnut-deep border border-lv-border"}`} data-testid="content-featured-toggle">
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-cream transition-transform ${form.is_featured ? "translate-x-5" : ""}`} />
          </button>
          <label className="text-xs text-lv-muted tracking-wide">Featured Content</label>
        </div>

        <button onClick={submit} disabled={saving} className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="content-submit-btn">
          <Save size={14} /> {saving ? "Creating..." : "Create Content (Draft)"}
        </button>
      </div>
    </div>
  );
}
